import { useEffect, useState } from "react";
import { Check, Loader2, CircleAlert, Circle } from "lucide-react";

/**
 * SaveIndicator — dot + text status pill for auto-save state.
 *
 * Variants (driven by `status` from useAutoSave):
 *  - idle      dim taupe ring · "all saved"
 *  - dirty     pulsing brass · "unsaved changes…"
 *  - saving    spinning brass · "saving…"
 *  - saved     solid brass · "saved · just now / 3 m ago / 12:04 pm"
 *  - error     red · "save failed · retry"
 *
 * Props:
 *  - status, lastSavedAt, lastError, flush — all from useAutoSave
 *  - testId — base testid so multiple pages can mount independent indicators
 */
export default function SaveIndicator({ status, lastSavedAt, lastError, flush, testId = "save-indicator" }) {
  const label = useRelativeTime(lastSavedAt, status);

  const renderDot = () => {
    if (status === "saving") return <Loader2 className="w-3 h-3 text-brass animate-spin" data-testid={`${testId}-dot`} />;
    if (status === "error") return <CircleAlert className="w-3 h-3 text-red-700" data-testid={`${testId}-dot`} />;
    if (status === "saved") return <Check className="w-3 h-3 text-brass" data-testid={`${testId}-dot`} />;
    if (status === "dirty") return <Circle className="w-2.5 h-2.5 text-brass fill-brass animate-pulse" data-testid={`${testId}-dot`} />;
    return <Circle className="w-2.5 h-2.5 text-taupe" data-testid={`${testId}-dot`} />;
  };

  const renderText = () => {
    if (status === "saving") return "saving…";
    if (status === "error") return (
      <span className="inline-flex items-center gap-1.5 text-red-700">
        save failed
        {flush && (
          <button onClick={flush} className="underline underline-offset-2 hover:text-red-800" data-testid={`${testId}-retry`}>retry</button>
        )}
      </span>
    );
    if (status === "saved") return `saved · ${label}`;
    if (status === "dirty") return "unsaved changes…";
    return "all saved";
  };

  const toneClass = status === "error" ? "text-red-700" : status === "saving" || status === "saved" || status === "dirty" ? "text-brass" : "text-taupe";

  return (
    <span
      className={`inline-flex items-center gap-2 text-[0.65rem] tracking-widest uppercase ${toneClass}`}
      data-testid={testId}
      data-status={status}
      title={lastError || undefined}
    >
      {renderDot()}
      <span>{renderText()}</span>
    </span>
  );
}

/** "just now" / "12s ago" / "4m ago" / "12:04 pm" — refreshes every 30s. */
function useRelativeTime(timestamp, status) {
  const [, tick] = useState(0);
  useEffect(() => {
    if (!timestamp) return undefined;
    const id = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, [timestamp]);
  if (status !== "saved" || !timestamp) return "";
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 10) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const d = new Date(timestamp);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }).toLowerCase();
}
